import { useState } from "react";
import { api } from "../lib/api";
import { fdate, money, run, useLoad } from "../lib/util";
import { Badge, Btn, Card, Field, Input, SectionTitle, Table } from "../components/kit";
import { useSchool } from "./ctx";
import { Downloads } from "./Forms";
import MapInfo from "./MapInfo";

function TestEmail({ saved }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    const to = (await api.get("/s/school")).data.mapa_email || saved;
    await run(() => api.post("/s/school/test-email"), `E-mail de teste enviado para ${to}`);
    setBusy(false);
  };
  return <Btn data-testid="settings-test-email-button" variant="outline" disabled={busy} onClick={send}>{busy ? "Enviando…" : "Enviar e-mail de teste"}</Btn>;
}

const SUB = { teste: "Em teste", ativa: "Ativa", em_atraso: "Em atraso", suspensa: "Suspensa", cancelada: "Cancelada" };

function Password() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => (await run(() => api.post("/auth/change-password", f), "Senha alterada")) && setF({ current_password: "", new_password: "" });
  return (
    <Card className="space-y-3">
      <h3 className="font-heading font-bold">Alterar minha senha</h3>
      <Field label="Senha atual"><Input data-testid="pwd-current-input" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
      <Field label="Nova senha (mín. 8)"><Input data-testid="pwd-new-input" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
      <Btn variant="navy" data-testid="pwd-save-button" onClick={save}>Alterar senha</Btn>
    </Card>
  );
}

export default function Settings() {
  const { school, tick } = useSchool();
  const [f, setF] = useState({ ref_date: school.ref_date || "", whatsapp: school.whatsapp || "", telefone: school.telefone || "", ua: school.ua || "", municipio: school.municipio || "", uf: school.uf || "", regional: school.regional || "", endereco: school.endereco || "", diretor_nome: school.diretor_nome || "", diretor_matricula: school.diretor_matricula || "", mapa_email: school.mapa_email || "" });
  const [logs] = useLoad(async () => (await api.get("/s/audit")).data, [tick]);
  const save = () => run(() => api.put("/s/school/settings", Object.fromEntries(Object.entries(f).filter(([k]) => school.acting || k !== "mapa_email").map(([k, v]) => [k, v || null]))), "Configurações salvas — recarregue para atualizar os relatórios");
  const logo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await run(() => api.post("/s/school/logo", fd), "Logomarca atualizada");
  };
  return (
    <div className="space-y-6">
      <SectionTitle title="Relatórios e configurações" subtitle="Os relatórios usam o município e a data de referência do cadastro da escola." />
      <Card>
        <h3 className="font-heading font-bold mb-3">Downloads do mês</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {[["oficio", "Ofício"], ["capa", "Mapa de controle do PAE (capa)"], ["cardapio_diario", "Cardápio diário"], ["controle_diario", "Controle diário de gêneros"], ["controle_mensal", "Controle mensal de estoque"], ["agricultura", "Controle da agricultura familiar"]].map(([k, l]) => (
            <div key={k} className="flex flex-wrap items-center justify-between gap-2 border rounded-lg p-3"><span className="font-semibold text-sm">{l}</span><Downloads form={k} label="PDF" /></div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">Os PDFs usam os modelos oficiais (iguais para todas as escolas), preenchidos com o cadastro da escola e os dados do mês. O PDF completo do mapa anexa ao final as notas fiscais e o cardápio confirmados no mês.</p>
      </Card>
      <MapInfo />
      <div className="grid lg:grid-cols-3 gap-5 [&>*]:min-w-0">
        <Card className="space-y-2 text-sm">
          <h3 className="font-heading font-bold">Cadastro da escola</h3>
          {[["Nome", school.name], ["Código", school.code], ["Regional", school.regional], ["Município/UF", `${school.municipio}/${school.uf}`], ["Gestor", school.gestor_name], ["E-mail", school.email]].map(([l, v]) => <div key={l}><span className="text-slate-500">{l}:</span> <b>{v || "—"}</b></div>)}
          <div><span className="text-slate-500">Assinatura:</span> <Badge tone="info">{SUB[school.subscription_status]}</Badge> {school.plan && <span>{school.plan.name} {school.plan.price != null && `· ${money(school.plan.price)}/${school.plan.period}`}</span>}</div>
          {school.payments?.length > 0 && <div className="pt-2 text-xs">{school.payments.slice(0, 6).map((p) => <div key={p.id}>{p.reference}: {money(p.amount)} — {p.status}</div>)}</div>}
        </Card>
        <Card className="space-y-3">
          <h3 className="font-heading font-bold">Configurações</h3>
          <Field label="Data de referência dos documentos"><Input data-testid="settings-refdate-input" type="date" value={f.ref_date} onChange={(e) => setF({ ...f, ref_date: e.target.value })} /></Field>
          <Field label="WhatsApp"><Input data-testid="settings-whatsapp-input" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></Field>
          <Field label="UA (unidade administrativa)"><Input data-testid="settings-ua-input" value={f.ua} onChange={(e) => setF({ ...f, ua: e.target.value })} /></Field>
          <Field label="Diretoria Regional (DRA)"><Input data-testid="settings-regional-input" placeholder="Ex.: Metropolitana IV" value={f.regional} onChange={(e) => setF({ ...f, regional: e.target.value })} /></Field>
          <Field label="Município"><Input data-testid="settings-municipio-input" value={f.municipio} onChange={(e) => setF({ ...f, municipio: e.target.value })} /></Field>
          <Field label="UF"><Input data-testid="settings-uf-input" maxLength={2} value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value.toUpperCase() })} /></Field>
          <Field label="Telefone"><Input data-testid="settings-telefone-input" value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} /></Field>
          <Field label="Endereço"><Input data-testid="settings-endereco-input" value={f.endereco} onChange={(e) => setF({ ...f, endereco: e.target.value })} /></Field>
          <Field label="Diretor(a) da U.E."><Input data-testid="settings-diretor-input" value={f.diretor_nome} onChange={(e) => setF({ ...f, diretor_nome: e.target.value })} /></Field>
          <Field label="Matrícula / ID do diretor"><Input data-testid="settings-matricula-input" value={f.diretor_matricula} onChange={(e) => setF({ ...f, diretor_matricula: e.target.value })} /></Field>
          {school.acting && <Field label="E-mail para envio do mapa"><Input data-testid="settings-mapa-email-input" type="email" placeholder="ex.: merenda.dra@exemplo.gov.br" value={f.mapa_email} onChange={(e) => setF({ ...f, mapa_email: e.target.value.trim() })} /></Field>}
          <Field label="Logomarca"><input data-testid="settings-logo-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files[0] && logo(e.target.files[0])} className="text-sm" /></Field>
          <div className="flex flex-wrap gap-2">
            <Btn data-testid="settings-save-button" onClick={save}>Salvar</Btn>
            {school.acting && <TestEmail saved={school.mapa_email} />}
          </div>
        </Card>
        {!school.acting && <Password />}
      </div>
      <div>
        <h3 className="font-heading font-bold text-lg mb-2">Histórico de alterações</h3>
        <Table testid="audit-table">
          <thead><tr><th>Quando</th><th>Usuário</th><th>Ação</th><th>Registro</th></tr></thead>
          <tbody>
            {(logs || []).map((l) => (
              <tr key={l.id}>
                <td className="whitespace-nowrap text-xs">{fdate(l.created_at)} {l.created_at.slice(11, 16)}</td>
                <td className="text-xs">{l.user_email}{l.acting_as_super && <Badge tone="orange">super admin</Badge>}</td>
                <td>{l.action}</td>
                <td className="text-xs">{l.entity} {l.details?.motivo || l.details?.reason || l.details?.filename || ""}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
